import { useEffect, useState, type ReactElement } from 'react';
import { RouterProvider } from 'react-router-dom';
import { DEFAULT_LANGUAGE } from '@models/i18n/language.model';
import {
  useAppStore,
  selectIsBootstrapped,
  selectIsLanguageLoading,
  selectIsLoading,
  selectFatalError,
  selectShowLandingHeaderStepper,
  selectShowMainFooter,
} from '@store/app.store';
import { FatalFallback } from '@shared/components/FatalFallback/FatalFallback';
import { bootstrapApplication } from '@bootstrap/app-bootstrap';
import { getLanguageFromPathname, normalizeLanguagePath } from '@utils/language-url.util';
import { useLanguageDirection } from '@hooks/useLanguageDirection';
import { useAppSetting } from '@hooks/useAppSetting';
import { WebSessionActionPopup } from '@features/passenger-details/components/WebSessionActionPopup/WebSessionActionPopup';
import { CheckinCountdownPopup } from '@shared/components/CheckinCountdownPopup/CheckinCountdownPopup';
import { sessionStorageUtil } from '@utils/session-storage.util';
import { SESSION_KEYS } from '@utils/session-keys.constants';
import {
  LEGACY_WEB_SESSION_QUERY_PARAM,
  WEB_SESSION_QUERY_PARAM,
  persistWebSessionId,
} from '@utils/web-session-query.constants';
import { router } from './router';
import { LanguageLoadingPopup } from '@shared/components';
import { ScrollResetOnNavigation } from '@shared/components/ScrollResetOnNavigation/ScrollResetOnNavigation';
import { FooterComponent, HeaderComponent } from '@shared/components/Layout';
import './_app.scss';

const GTM_NOSCRIPT_ID = 'gtm-noscript';
const DEFAULT_GTM_ID = 'GTM-WGG3X6';

let injectedGtmId: string | null = null;

const injectGtmScript = (gtmId: string): void => {
  if (injectedGtmId === gtmId) {
    return;
  }
  document.getElementById(GTM_NOSCRIPT_ID)?.remove();
  injectedGtmId = gtmId;

  const script = document.createElement('script');
  script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');`;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  noscript.id = GTM_NOSCRIPT_ID;
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  iframe.title = 'Google Tag Manager';
  noscript.appendChild(iframe);
  document.body.prepend(noscript);
};

const syncWebSessionIdInUrl = (): void => {
  const params = new URLSearchParams(globalThis.location.search);
  const webSessionId =
    params.get(WEB_SESSION_QUERY_PARAM) ?? params.get(LEGACY_WEB_SESSION_QUERY_PARAM);

  if (webSessionId) {
    sessionStorageUtil.set(SESSION_KEYS.WEB_SESSION_ID, webSessionId.trim());
    if (!params.get(WEB_SESSION_QUERY_PARAM)) {
      persistWebSessionId(webSessionId);
    }
    return;
  }

  const storedWebSessionId = sessionStorageUtil.get<string>(SESSION_KEYS.WEB_SESSION_ID);
  if (storedWebSessionId) {
    persistWebSessionId(storedWebSessionId);
  }
};

export const App = (): ReactElement => {
  const isBootstrapped = useAppStore(selectIsBootstrapped);
  const isLanguageLoading = useAppStore(selectIsLanguageLoading);
  const isLoading = useAppStore(selectIsLoading);
  const fatalError = useAppStore(selectFatalError);
  const showLandingHeaderStepper = useAppStore(selectShowLandingHeaderStepper);
  const showMainFooter = useAppStore(selectShowMainFooter);
  const [headerRouteKey, setHeaderRouteKey] = useState(globalThis.location.pathname);
  const gtmId = useAppSetting('gtmId', DEFAULT_GTM_ID);

  useLanguageDirection();

  useEffect(() => {
    injectGtmScript(gtmId);
  }, [gtmId]);

  useEffect(
    () =>
      router.subscribe((state) => {
        setHeaderRouteKey(state.location.pathname);
      }),
    [],
  );

  useEffect(() => {
    const normalizedPath = normalizeLanguagePath(globalThis.location.pathname);

    if (normalizedPath !== globalThis.location.pathname) {
      globalThis.history.replaceState(
        null,
        '',
        `${normalizedPath}${globalThis.location.search}${globalThis.location.hash}`,
      );
    }

    syncWebSessionIdInUrl();

    const initialLanguage =
      getLanguageFromPathname(globalThis.location.pathname) ?? DEFAULT_LANGUAGE;

    void bootstrapApplication(initialLanguage);
  }, []);

  if (fatalError) {
    return <FatalFallback message={fatalError} />;
  }

  if (isLoading || !isBootstrapped) {
    return <LanguageLoadingPopup />;
  }

  return (
    <div className="main">
      <ScrollResetOnNavigation />
      <HeaderComponent key={headerRouteKey} showHeaderStepper={showLandingHeaderStepper} />
      <div className="main__content">
        <RouterProvider router={router} />
      </div>
      {showMainFooter ? <FooterComponent /> : null}
      {isLanguageLoading ? <LanguageLoadingPopup /> : null}
      <WebSessionActionPopup />
      <CheckinCountdownPopup />
    </div>
  );
};
